import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Trash2, Inbox } from "lucide-react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";

const STATUSES = ["new", "contacted", "admitted", "closed"];
const STATUS_STYLE = {
  new: "bg-terracotta/15 text-terracotta",
  contacted: "bg-sage/25 text-forest",
  admitted: "bg-forest text-sand",
  closed: "bg-line text-ink2",
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, admitted: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/leads", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/leads/stats"),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const changeStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    toast.success(`Marked as ${status}`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    load();
  };

  const cards = [
    { label: "Total enquiries", value: stats.total },
    { label: "New", value: stats.new },
    { label: "Contacted", value: stats.contacted },
    { label: "Admitted", value: stats.admitted },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-leads-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-4xl text-forest">Admissions Inbox</h1>
            <p className="text-ink2 mt-1">Every enquiry from your website, in one place.</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="leads-filter" className="w-48 bg-white border-line rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-white border border-line p-6">
              <p className="text-3xl font-serif text-forest">{c.value}</p>
              <p className="text-sm text-ink2 mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl bg-white border border-line overflow-hidden">
          {loading ? (
            <p className="p-8 text-ink2">Loading…</p>
          ) : leads.length === 0 ? (
            <div className="p-16 text-center text-ink2">
              <Inbox className="w-10 h-10 mx-auto mb-4 text-line" />
              No enquiries yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-sand2/60">
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Interest</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell>
                        <p className="font-semibold text-ink">{l.name}</p>
                        <p className="text-xs text-ink2">{new Date(l.created_at).toLocaleString()}</p>
                        {l.relationship_to_client && <p className="text-xs text-sage mt-1">For: {l.relationship_to_client}</p>}
                      </TableCell>
                      <TableCell>
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-forest text-sm"><Phone className="w-3 h-3" /> {l.phone}</a>
                        {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-ink2 text-sm mt-1"><Mail className="w-3 h-3" /> {l.email}</a>}
                      </TableCell>
                      <TableCell className="text-sm text-ink2">{l.program_interest || "—"}</TableCell>
                      <TableCell className="max-w-[240px] text-sm text-ink2"><p className="line-clamp-2">{l.message || "—"}</p></TableCell>
                      <TableCell>
                        <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                          <SelectTrigger data-testid={`lead-status-${l.id}`} className="w-32 h-9 border-line rounded-lg">
                            <Badge className={`${STATUS_STYLE[l.status]} capitalize border-0`}>{l.status}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`lead-delete-${l.id}`} className="text-ink2 hover:text-destructive p-2"><Trash2 className="w-4 h-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
                              <AlertDialogDescription>This permanently removes {l.name}’s enquiry. This cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(l.id)} className="bg-destructive text-white">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminLeads;
